import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import Toggle from '../../components/ui/Toggle';
import { cn } from '../../utils/utils';

/** One editable checklist item: a stable key plus its DE/EN label. */
export interface ChecklistItemValue {
    key: string;
    de: string;
    en: string;
}

/**
 * One editable checklist.
 *
 * `authored` is the difference between "the tenant never touched this" and "the
 * tenant deliberately wants a checklist with no items". Off, the payload omits
 * the variant entirely and the end-user app shows its per-use-case defaults; on
 * with an empty `items`, it shows the lump-sum screen. Collapsing the two would
 * make the lump-sum checklist unreachable.
 */
export interface ChecklistValue {
    authored: boolean;
    title: { de: string, en: string };
    description: { de: string, en: string };
    items: ChecklistItemValue[];
}

export const emptyChecklist = (): ChecklistValue => ({
    authored: false,
    title: { de: '', en: '' },
    description: { de: '', en: '' },
    items: [],
});

/** Mirrors `CHECKLIST_MAX_ITEMS` in `new-backend/admin/src/lib/tenantConfig.ts`,
 *  which rejects anything beyond it — the two must move together. */
export const CHECKLIST_MAX_ITEMS = 20;

const textFieldClasses = cn(
    'py-2 px-4 rounded-xl text-foreground bg-transparent w-full',
    'border border-foreground transition-all outline-none focus:border-primary',
);

interface ChecklistEditorProps {
    label: string;
    value: ChecklistValue;
    onChange: (next: ChecklistValue) => void;
    readOnly?: boolean;
}

/**
 * Editor for one authored checklist (check-in or check-out) of one use case.
 *
 * **The item count is the mode** (PO, 2026-09-04). A tenant who adds no items
 * gets the lump-sum screen — "look around, tell us if anything is off", with a
 * link into the problem report — and one who adds items gets the itemised list.
 * There is deliberately no mode selector: two ways to say the same thing is two
 * ways for them to disagree, and the hint below states which one is active
 * rather than asking the tenant to choose it twice.
 *
 * Item keys are generated once and never re-derived from the label, because the
 * end-user app keys its flag state on them: re-wording an item must not read as
 * a different item.
 */
const ChecklistEditor = ({ label, value, onChange, readOnly = false }: ChecklistEditorProps) => {
    const { t } = useTranslation();

    const setField = (
        field: 'title' | 'description',
        lang: 'de' | 'en',
        text: string,
    ) => onChange({ ...value, [field]: { ...value[field], [lang]: text } });

    const setItem = (index: number, lang: 'de' | 'en', text: string) =>
        onChange({
            ...value,
            items: value.items.map((item, i) =>
                i === index ? { ...item, [lang]: text } : item,
            ),
        });

    const addItem = () =>
        onChange({
            ...value,
            items: [
                ...value.items,
                // Unique for the lifetime of the row; the backend re-assigns
                // positionally only if it is missing or duplicated.
                { key: `item_${Date.now().toString(36)}`, de: '', en: '' },
            ],
        });

    const removeItem = (index: number) =>
        onChange({ ...value, items: value.items.filter((_, i) => i !== index) });

    return (
        <div className={'flex flex-col gap-3'}>
            <Toggle
                label={label}
                description={t('account.checkin_checklist_authored_desc')}
                checked={value.authored}
                onChange={(next) => onChange({ ...value, authored: next })}
                disabled={readOnly}
            />

            {value.authored && (
                <div className={'flex flex-col gap-4 pl-4 border-l-2 border-foreground/10'}>
                    <div className={'flex flex-col gap-1.5'}>
                        <label className={'text-sm text-foreground/70 font-medium'}>
                            {t('account.checkin_checklist_title_label')}
                        </label>
                        {(['de', 'en'] as const).map((lang) => (
                            <div key={lang} className={'flex items-center gap-2'}>
                                <span className={'text-xs text-foreground/40 w-6 uppercase'}>
                                    {lang}
                                </span>
                                <input
                                    type={'text'}
                                    className={textFieldClasses}
                                    value={value.title[lang]}
                                    onChange={(e) => setField('title', lang, e.target.value)}
                                    placeholder={t('account.checkin_checklist_title_placeholder')}
                                    disabled={readOnly}
                                />
                            </div>
                        ))}
                    </div>

                    <div className={'flex flex-col gap-1.5'}>
                        <label className={'text-sm text-foreground/70 font-medium'}>
                            {t('account.checkin_checklist_description_label')}
                        </label>
                        {(['de', 'en'] as const).map((lang) => (
                            <div key={lang} className={'flex items-center gap-2'}>
                                <span className={'text-xs text-foreground/40 w-6 uppercase'}>
                                    {lang}
                                </span>
                                <input
                                    type={'text'}
                                    className={textFieldClasses}
                                    value={value.description[lang]}
                                    onChange={(e) => setField('description', lang, e.target.value)}
                                    placeholder={t(
                                        'account.checkin_checklist_description_placeholder',
                                    )}
                                    disabled={readOnly}
                                />
                            </div>
                        ))}
                    </div>

                    <div className={'flex flex-col gap-2'}>
                        <label className={'text-sm text-foreground/70 font-medium'}>
                            {t('account.checkin_checklist_items_label')}
                        </label>
                        {value.items.map((item, index) => (
                            <div key={item.key} className={'flex flex-col gap-1.5'}>
                                {(['de', 'en'] as const).map((lang) => (
                                    <div key={lang} className={'flex items-center gap-2'}>
                                        <span
                                            className={'text-xs text-foreground/40 w-6 uppercase'}
                                        >
                                            {lang}
                                        </span>
                                        <input
                                            type={'text'}
                                            className={textFieldClasses}
                                            value={item[lang]}
                                            onChange={(e) => setItem(index, lang, e.target.value)}
                                            placeholder={t(
                                                'account.checkin_checklist_item_placeholder',
                                            )}
                                            disabled={readOnly}
                                        />
                                        {lang === 'de' && !readOnly && (
                                            <button
                                                type={'button'}
                                                onClick={() => removeItem(index)}
                                                aria-label={t('account.checkin_checklist_remove')}
                                                className={
                                                    'p-2 text-foreground/50 hover:text-foreground'
                                                }
                                            >
                                                <Trash2 className={'w-4 h-4'} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ))}
                        {!readOnly && value.items.length < CHECKLIST_MAX_ITEMS && (
                            <button
                                type={'button'}
                                onClick={addItem}
                                className={
                                    'self-start flex items-center gap-1.5 text-sm text-primary underline'
                                }
                            >
                                <Plus className={'w-4 h-4'} />
                                {t('account.checkin_checklist_add_item')}
                            </button>
                        )}
                        {/* States which screen the current item count produces,
                            rather than asking the tenant to pick a mode. */}
                        <p className={'text-xs text-foreground/60'}>
                            {value.items.length === 0
                                ? t('account.checkin_checklist_summary_hint')
                                : t('account.checkin_checklist_items_hint')}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChecklistEditor;
